
import {
  NovelActSchema,
  NovelChapterSchema,
  NovelSceneSchema,
  NovelSchema,
  CodexEntrySchema,
  CoreEntityType,
  CodexCategorySchema,
  CodexFieldSchema,
} from "#/index";
import { CodexDetail, ExecutionContext, NovelLabel, OutlineTree, PointOfView } from "../types";

const PointOfViewUtils = class {
  context: ExecutionContext;
  result: PointOfView[] = [];
  private mode: 'union' | 'intersection' = 'union';
  private initialized: boolean = false;

  constructor(context: ExecutionContext, mode: 'union' | 'intersection' = 'union') {
    this.context = context;
    this.mode = mode;
  }

  useUnion(reset: boolean = true): this {
    this.mode = 'union';
    if (reset) {
      this.result = [];
      this.initialized = false;
    }
    return this;
  }

  useIntersection(reset: boolean = true): this {
    this.mode = 'intersection';
    if (reset) {
      this.result = [];
      this.initialized = false;
    }
    return this;
  }

  private toKey(item: PointOfView): string {
    return `${item.value}::${item.characterId ?? ''}`;
  }

  private dedupe(items: PointOfView[]): PointOfView[] {
    const map = new Map<string, PointOfView>();
    items.forEach(item => {
      map.set(this.toKey(item), item);
    });
    return Array.from(map.values());
  }

  private applyFilter(items: PointOfView[]): void {
    const povs = this.dedupe(items.filter(item => !!item.value));

    if (this.mode === 'union') {
      const existed = new Set(this.result.map(item => this.toKey(item)));
      povs.forEach(item => {
        const key = this.toKey(item);
        if (!existed.has(key)) {
          existed.add(key);
          this.result.push(item);
        }
      });
      return;
    }

    if (!this.initialized) {
      this.result = povs;
      this.initialized = true;
      return;
    }

    const candidateKeys = new Set(povs.map(item => this.toKey(item)));
    this.result = this.result.filter(item => candidateKeys.has(this.toKey(item)));
  }

  private getAllPov(): PointOfView[] {
    const data: PointOfView[] = [];
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    const currentNovel = this.context!.globalContext.currentPosition?.novel;
    novelScenes.forEach(scene => {
      if (scene.relationships.project.type === 'novels' && scene.relationships.project.id === currentNovel?.id) {
        if (scene.attributes.pov?.type) {
          data.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov?.characterId
          });
        }
      }
    });
    if (currentNovel?.attributes.pov?.type) {
      data.push({
        type: 'pointOfView',
        value: currentNovel.attributes.pov.type,
        characterId: currentNovel.attributes.pov?.characterId
      });
    }
    return data;
  }

  byOutline(outline: OutlineTree): this {
    const povs: PointOfView[] = [];
    outline.data.forEach(act => {
      act.children?.forEach(chapter => {
        chapter.children?.forEach(scene => {
          if (scene.data.type === CoreEntityType.NovelScenes && scene.data.attributes.pov?.type) {
            povs.push({
              type: 'pointOfView',
              value: scene.data.attributes.pov.type,
              characterId: scene.data.attributes.pov?.characterId,
            });
          }
        });
      });
    });
    this.applyFilter(povs);
    return this;
  }

  byNovel(novel: NovelSchema): this {
    const povs: PointOfView[] = [];
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    novelScenes.forEach(scene => {
      if (scene.relationships.project.type === 'novels' && scene.relationships.project.id === novel.id) {
        if (scene.attributes.pov?.type) {
          povs.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov?.characterId,
          });
        }
      }
    });
    if (novel.attributes.pov?.type) {
      povs.push({
        type: 'pointOfView',
        value: novel.attributes.pov.type,
        characterId: novel.attributes.pov?.characterId,
      });
    }
    this.applyFilter(povs);
    return this;
  }
  byAct(act: NovelActSchema): this {
    const povs: PointOfView[] = [];
    const { novelActsTree } = this.context!.globalContext.generateStore.getState();
    const actTree = novelActsTree.find(d => d.id === act.id);
    actTree?.children?.forEach(chapter => {
      chapter.children?.forEach(scene => {
        if (scene.data.type === CoreEntityType.NovelScenes && scene.data.attributes.pov?.type) {
          povs.push({
            type: 'pointOfView',
            value: scene.data.attributes.pov.type,
            characterId: scene.data.attributes.pov?.characterId,
          });
        }
      })
    })
    this.applyFilter(povs);
    return this;
  }
  byChapter(chapter: NovelChapterSchema): this {
    const povs: PointOfView[] = [];
    const { novelActsTree } = this.context!.globalContext.generateStore.getState();
    novelActsTree.forEach(act => {
      if (act.childIds?.includes(chapter.id)) {
        act.children?.forEach(chapter => {
          chapter.children?.forEach(scene => {
            if (scene.data.type === CoreEntityType.NovelScenes && scene.data.attributes.pov?.type) {
              povs.push({
                type: 'pointOfView',
                value: scene.data.attributes.pov.type,
                characterId: scene.data.attributes.pov?.characterId,
              });
            }
          })
        })
      }
    })
    this.applyFilter(povs);
    return this;
  }
  byScene(scene: NovelSceneSchema): this {
    if (scene.attributes.pov?.type) {
      this.applyFilter([{
        type: 'pointOfView',
        value: scene.attributes.pov.type,
        characterId: scene.attributes.pov?.characterId,
      }]);
    }
    return this;
  }
  byPov(pov: PointOfView): this {
    this.applyFilter([pov]);
    return this;
  }
  byLabel(label: NovelLabel): this {
    const povs: PointOfView[] = [];
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    novelScenes.forEach(scene => {
      if (scene.relationships.labels.includes(label.data.id)) {
        if (scene.attributes.pov?.type) {
          povs.push({
            type: 'pointOfView',
            value: scene.attributes.pov.type,
            characterId: scene.attributes.pov?.characterId,
          });
        }
      }
    })
    this.applyFilter(povs);
    return this;
  }
  byCodexEntry(entry: CodexEntrySchema): this {
    const matched = this.getAllPov().filter(pov => pov.characterId === entry.id);
    this.applyFilter(matched);
    return this;
  }
  byCodexCategory(category: CodexCategorySchema): this {
    const { codexEntryGroups } = this.context!.globalContext.generateStore.getState()
    const pov = this.getAllPov();
    const group = codexEntryGroups.find(group => group.category?.id === category.id)
    const codexIds = group?.children.map(entry => entry.id) || [];
    const matched = pov.filter(d => d.characterId && codexIds.includes(d.characterId));
    this.applyFilter(matched);
    return this;
  }
  byCodexDetail(detail: CodexDetail): this {
    const pov = this.getAllPov();
    const matched = pov.filter(d => d.characterId && detail.entry.id === d.characterId);
    this.applyFilter(matched);
    return this;
  }
  getResult(): PointOfView[] {
    return this.result;
  }
}

export default PointOfViewUtils;